import { get } from 'lodash'
import { decode } from 'he'
import {
  MAX_SUBENTITIES,
  INDIVIDUAL_FORM_TYPE,
  INTERESTED_PARTY_FORM_TYPE,
} from '../constants'

const INDIVIDUAL_KEY = 'individuals'
const INTERESTED_PARTY_KEY = 'interested_parties'

const createInterestedPartyToFE = entityData => {
  const interestedParty = {
    entityName: decode(entityData.entity_name || ''),
    entityNameText: decode(entityData.entity_name || ''),
    state: entityData.entity_state || '',
    entityType: entityData.entity_sub_type || '',
    interestedPartyTitle: entityData.entity_title || '',
    moreEntityInvolved: entityData.more_entity_involved,
    moreIndividualInvolved: entityData.more_signer_involved,
    interestedPartyOwnership: entityData.percent_stake || 0,
    interestedParties: [],
  }

  if (entityData.entity_title === 'OTHER') {
    interestedParty.interestedPartyOtherTitle =
      entityData.entity_title_other_desc
  }

  interestedParty.interestedParties = []
  return interestedParty
}

const createIndividualToFE = entityData => {
  const individual = {
    name: decode(entityData.signer_name || ''),
    nameText: decode(entityData.signer_name || ''),
    individualTitle: entityData.signer_title || '',
    individualOwnership: entityData.percent_stake || 0,
    includeSignature: entityData.signer,
    email: entityData.signer_email || '',
  }
  if (entityData.signer_title === 'OTHER') {
    individual.individualOtherTitle = entityData.signer_title_other
  }
  return individual
}

const transformEntityToFE = entityData => {
  // the current entity is an interested party if it has children
  const formType =
    entityData[INTERESTED_PARTY_KEY] || entityData[INDIVIDUAL_KEY]
      ? INTERESTED_PARTY_FORM_TYPE
      : INDIVIDUAL_FORM_TYPE

  const entity =
    formType === INTERESTED_PARTY_FORM_TYPE
      ? createInterestedPartyToFE(entityData)
      : createIndividualToFE(entityData)

  if (formType === INDIVIDUAL_FORM_TYPE) {
    return entity
  }

  for (let i = 0; i < entityData.interested_parties.length; i++) {
    const party = transformEntityToFE(entityData.interested_parties[i])
    party.subFormType = INTERESTED_PARTY_FORM_TYPE
    entity.interestedParties.push(party)
  }

  for (let i = 0; i < entityData.individuals.length; i++) {
    const individual = transformEntityToFE(entityData.individuals[i])
    individual.subFormType = INDIVIDUAL_FORM_TYPE
    entity.interestedParties.push(individual)
  }
  return entity
}

export const entityAPItoFE = entityData => {
  if (!entityData) {
    return { parent: {} }
  }
  const parent = transformEntityToFE(entityData)
  return { parent }
}

const createInterestedPartyToAPI = entityData => {
  const hasMaxSubEntity =
    get(entityData, 'interestedParties', []).length === MAX_SUBENTITIES
  const interestedParty = {
    entity_type: 'ENTITY',
    entity_name: entityData.entityName || '',
    entity_state: entityData.state || '',
    entity_sub_type: entityData.entityType || '',
    entity_title: entityData.interestedPartyTitle || '',
    more_entity_involved:
      hasMaxSubEntity && Boolean(entityData.moreEntityInvolved),
    more_signer_involved:
      hasMaxSubEntity && Boolean(entityData.moreIndividualInvolved),
    percent_stake: entityData.interestedPartyOwnership || 0,
    [INDIVIDUAL_KEY]: [], // only interested parties can have individual or interested party children
    [INTERESTED_PARTY_KEY]: [],
  }
  if (entityData.interestedPartyTitle === 'OTHER') {
    interestedParty.entity_title_other_desc =
      entityData.interestedPartyOtherTitle
  }
  return interestedParty
}

const createIndividualToAPI = entityData => {
  const individual = {
    signer_name: entityData.name || '',
    signer_title: entityData.individualTitle || '',
    percent_stake: entityData.individualOwnership || 0,
    signer: Boolean(entityData.includeSignature),
    signer_email: entityData.email || '',
  }
  if (entityData.individualTitle === 'OTHER') {
    individual.signer_title_other = entityData.individualOtherTitle
  }
  return individual
}

const transformEntityToAPI = entityData => {
  const subFormType = entityData.subFormType

  const entity =
    subFormType === INTERESTED_PARTY_FORM_TYPE
      ? createInterestedPartyToAPI(entityData)
      : createIndividualToAPI(entityData)

  if (subFormType === INDIVIDUAL_FORM_TYPE) {
    return entity
  }

  const interestedParties = entityData.interestedParties

  for (let i = 0; i < interestedParties.length; i++) {
    const subParty = transformEntityToAPI(entityData.interestedParties[i])
    const partyKey =
      interestedParties[i].subFormType === INTERESTED_PARTY_FORM_TYPE
        ? INTERESTED_PARTY_KEY
        : INDIVIDUAL_KEY
    entity[partyKey].push(subParty)
  }

  return entity
}

export const entityFEtoAPI = entityData => {
  // the parent form is by default an interested party form
  entityData.parent.subFormType = INTERESTED_PARTY_FORM_TYPE
  entityData.parent.interestedPartyOwnership = 100
  return transformEntityToAPI(entityData.parent)
}
